import type { ToolName, ToolRef } from './tools/registry';
import type { PlannerToolPart, PlannerUIMessage, PlannerUITools } from './types';

// A tool's part, typed as that tool's own: its input and output need no cast at the call site.
export type ToolPartOf<NAME extends ToolName> = Extract<PlannerToolPart, { type: `tool-${NAME}` }>;

export type ToolOutputOf<NAME extends ToolName> = PlannerUITools[NAME]['output'];

/**
 * The part type the SDK gives a tool's calls. The prefix is written here and nowhere
 * else, and the tool is a registry entry, so nothing outside the registry can name one.
 */
export const toolPartType = <NAME extends ToolName>(tool: ToolRef<NAME>): `tool-${NAME}` =>
  `tool-${tool.name}`;

// Whether the part is one of the tool's calls, whatever its state.
export const isToolPartOf = <NAME extends ToolName>(
  part: { type: string },
  tool: ToolRef<NAME>,
): part is ToolPartOf<NAME> => part.type === toolPartType(tool);

// A tool's parts on a message, in call order, typed as the tool's own.
export const toolParts = <NAME extends ToolName>(
  message: PlannerUIMessage,
  tool: ToolRef<NAME>,
): ToolPartOf<NAME>[] =>
  (message.parts ?? []).filter((part): part is ToolPartOf<NAME> => isToolPartOf(part, tool));

// Whether the message holds any call of the tool, answered or not.
export const hasToolPart = (message: PlannerUIMessage, tool: ToolRef): boolean =>
  toolParts(message, tool).length > 0;

/**
 * Whether the call has its result: the state says so, and the output is the record the
 * type promises. A stored output is whatever was written, so one that is not a record
 * reads as a call without a result and nothing reaches into it.
 */
export const isFinished = <PART extends { state: string; output?: unknown }>(
  part: PART,
): part is Extract<PART, { state: 'output-available' }> =>
  part.state === 'output-available' && typeof part.output === 'object' && part.output !== null;
